import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { CalculatorInput, CalculatorResult, CalculatorResultsGrid } from '@/components/calculators'
import {
  calculateMonthlyPayment,
  calculateCashOnCash,
  calculateMonthlyCashFlow,
  calculateCapRate,
  formatCurrency,
  formatPercent,
} from '@/lib/calculators/formulas'

export default function CashOnCashCalculator() {
  const [purchasePrice, setPurchasePrice] = useState(200000)
  const [downPaymentPercent, setDownPaymentPercent] = useState(25)
  const [closingCosts, setClosingCosts] = useState(5000)
  const [rehabCosts, setRehabCosts] = useState(0)
  const [monthlyRent, setMonthlyRent] = useState(2400)
  const [vacancyRate, setVacancyRate] = useState(8)
  const [monthlyExpenses, setMonthlyExpenses] = useState(400)
  const [interestRate, setInterestRate] = useState(7)
  const [loanTerm, setLoanTerm] = useState(30)

  const downPayment = purchasePrice * (downPaymentPercent / 100)
  const loanAmount = purchasePrice - downPayment
  const totalCashInvested = downPayment + closingCosts + rehabCosts
  const monthlyDebtService = calculateMonthlyPayment(loanAmount, interestRate, loanTerm)
  const monthlyCashFlow = calculateMonthlyCashFlow(monthlyRent, vacancyRate, monthlyExpenses, monthlyDebtService)
  const annualCashFlow = monthlyCashFlow * 12
  const cashOnCash = calculateCashOnCash(annualCashFlow, totalCashInvested)
  const effectiveGrossIncome = monthlyRent * 12 * (1 - vacancyRate / 100)
  const noi = effectiveGrossIncome - monthlyExpenses * 12
  const capRate = calculateCapRate(noi, purchasePrice)

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 py-12">
      <Link to="/resources/calculators" className="text-amber-600 hover:underline">
        &larr; Back to Calculators
      </Link>
      <h1 className="text-3xl font-bold">Cash-on-Cash Return Calculator</h1>

      <Card>
        <CardContent className="space-y-4 p-6">
          <p>
            Cash-on-cash return measures your annual pre-tax cash flow as a percentage of the total cash you invested.
            It answers the question: what return am I getting on the actual dollars I put in?
          </p>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardContent className="space-y-4 p-6">
            <h2 className="text-lg font-semibold">Acquisition</h2>

            <CalculatorInput label="Purchase Price" value={purchasePrice} onChange={setPurchasePrice} prefix="$" />

            <CalculatorInput
              label="Down Payment"
              value={downPaymentPercent}
              onChange={setDownPaymentPercent}
              suffix="%"
              tooltip="Investment properties typically require 20-25% down"
            />

            <CalculatorInput label="Closing Costs" value={closingCosts} onChange={setClosingCosts} prefix="$" />

            <CalculatorInput label="Rehab / Repair Costs" value={rehabCosts} onChange={setRehabCosts} prefix="$" />

            <div className="text-muted-foreground text-sm">
              Total cash invested: {formatCurrency(totalCashInvested)}
            </div>

            <Separator />

            <h2 className="text-lg font-semibold">Loan Terms</h2>

            <CalculatorInput label="Interest Rate" value={interestRate} onChange={setInterestRate} suffix="%" />

            <div className="space-y-1.5">
              <span className="text-sm font-medium">Loan Term</span>
              <div className="flex gap-2" role="group" aria-label="Loan Term">
                {[15, 20, 30].map((term) => (
                  <button
                    key={term}
                    onClick={() => setLoanTerm(term)}
                    className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                      loanTerm === term ? 'bg-amber-500 text-white' : 'bg-muted hover:bg-muted/80'
                    }`}
                  >
                    {term} years
                  </button>
                ))}
              </div>
            </div>

            <Separator />

            <h2 className="text-lg font-semibold">Income & Expenses</h2>

            <CalculatorInput label="Monthly Gross Rent" value={monthlyRent} onChange={setMonthlyRent} prefix="$" />

            <CalculatorInput
              label="Vacancy Rate"
              value={vacancyRate}
              onChange={setVacancyRate}
              suffix="%"
              tooltip="Typical: 5-10% for stable markets"
            />

            <CalculatorInput
              label="Monthly Operating Expenses"
              value={monthlyExpenses}
              onChange={setMonthlyExpenses}
              prefix="$"
              tooltip="Taxes, insurance, maintenance, management, etc."
            />
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-4 p-6">
            <h2 className="text-lg font-semibold">Returns</h2>

            <CalculatorResult
              label="Cash-on-Cash Return"
              value={formatPercent(cashOnCash)}
              highlight
              subtext="Annual cash flow / total cash invested"
            />

            <CalculatorResultsGrid>
              <CalculatorResult label="Monthly Cash Flow" value={formatCurrency(monthlyCashFlow)} />
              <CalculatorResult label="Annual Cash Flow" value={formatCurrency(annualCashFlow)} />
            </CalculatorResultsGrid>

            <Separator />

            <h2 className="text-lg font-semibold">Other Metrics</h2>

            <CalculatorResultsGrid>
              <CalculatorResult label="Cap Rate" value={formatPercent(capRate)} subtext="NOI / Purchase Price" />
              <CalculatorResult
                label="Net Operating Income"
                value={formatCurrency(noi)}
                subtext="Annual (before debt)"
              />
              <CalculatorResult label="Monthly Debt Service" value={formatCurrency(monthlyDebtService)} />
              <CalculatorResult label="Total Cash Invested" value={formatCurrency(totalCashInvested)} />
            </CalculatorResultsGrid>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
